import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ApiClient, MaschineVerwaltung, NeueMaschine, Maschinenbestand,
  Maschinenart, Betriebsstufe, MaschinenKontingent, Projekt,
} from '../api';
import { MASCHINENARTEN, maschinenartText, maschinenfaehigkeitText } from '../api';
import { Arbeitsstationen } from './Arbeitsstationen';
import { inDieAblage } from '../kopieren';
import { vorWieLange } from './Zustandsseite';
import {
  IconMaschine, IconPlus, IconSchluessel, IconHerunterladen,
} from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ConfirmDialog, DataTable, Drawer, EmptyState, ErrorState,
  Feld, FormSection, LoadingState, PropertyGrid, StatusBadge,
  type Kennwert, type Spalte, type Zustand,
} from '../muster';

export function Maschinen({ api }: { api: ApiClient }) {
  const [bestand, setBestand] = useState<Maschinenbestand | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [anlegen, setAnlegen] = useState(false);

  // Nach dem Anlegen oder Erneuern: Der Schlüssel steht genau hier und
  // nirgends sonst. Verschwindet er, ist er weg.
  const [schluessel, setSchluessel] = useState<NeueMaschine | null>(null);

  /** Golden-Attestierung im Schubfach - sie hat zwölf Kennwerte (Abschnitt 16). */
  const [details, setDetails] = useState<MaschineVerwaltung | null>(null);

  /** Nachfrage und laufende Zeilenaktion. */
  const [nachfrage, setNachfrage] =
    useState<{ maschine: MaschineVerwaltung; art: 'stilllegen' | 'entfernen' } | null>(null);
  const [aktionsfehler, setAktionsfehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  /** Zugangsschlüssel erneuern - eigene Nachfrage, weil sie eine Verbindung kappt. */
  const [schluesselfrage, setSchluesselfrage] = useState<MaschineVerwaltung | null>(null);

  /** MASCH-1 (F209 E6): Filter nach Maschinenart, leer = alle. */
  const [artFilter, setArtFilter] = useState<Maschinenart | ''>('');

  const laden = useCallback(() => {
    setFehler(null);
    return api.maschinenVerwalten().then(setBestand).catch(setFehler);
  }, [api]);

  useEffect(() => { laden(); }, [laden]);

  const handeln = useCallback(async (was: () => Promise<unknown>) => {
    setLaeuft(true);
    setAktionsfehler(null);
    try {
      await was();
      await laden();
    } catch (e) {
      setAktionsfehler(e);
    } finally {
      setLaeuft(false);
    }
  }, [laden]);

  const spalten: Spalte<MaschineVerwaltung>[] = useMemo(() => [
    {
      schluessel: 'name', titel: 'Name', immerSichtbar: true,
      wert: (m) => m.name,
      inhalt: (m) => (
        <>
          <strong>{m.name}</strong>
          {istGolden(m) && (
            <div className="sehr-schwach" style={{ marginTop: 3, whiteSpace: 'nowrap' }}>
              {m.machineName ?? 'physische Maschine unbekannt'}
              {' · '}{m.goldenRole ?? 'Rolle unbekannt'}
            </div>
          )}
        </>
      ),
    },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', breite: '160px',
      wert: (m) => maschinenzustand(m).text,
      inhalt: (m) => {
        const z = maschinenzustand(m);
        return (
          <>
            <StatusBadge zustand={z.zustand} text={z.text} klein />
            {istGolden(m) && (
              <div style={{ marginTop: 4 }}>
                <StatusBadge
                  zustand={goldenBereit(m) ? 'gut' : 'fehler'}
                  text={goldenBereit(m) ? 'Golden READY' : 'Golden NOT READY'}
                  klein
                />
              </div>
            )}
          </>
        );
      },
    },
    {
      schluessel: 'lebenszeichen', titel: 'Lebenszeichen', breite: '140px',
      wert: (m) => m.lastHeartbeat ?? '',
      inhalt: (m) => (
        <span className="schwach" title={m.lastHeartbeat ?? undefined}>
          {vorWieLange(m.lastHeartbeat)}
        </span>
      ),
    },
    /* MASCH-1 (F206, F209 E6): Art und Stufe. Ein älterer Server liefert sie
       nicht - dann ist jede Maschine wie bisher Revit im Produktivbetrieb. */
    {
      schluessel: 'art', titel: 'Art', breite: '130px',
      wert: (m) => maschinenartText(art(m)),
      inhalt: (m) => (
        <StatusBadge
          zustand={art(m) === 'REVIT' ? 'neutral' : 'info'}
          text={maschinenartText(art(m))}
          klein
        />
      ),
    },
    {
      schluessel: 'stufe', titel: 'Stufe', breite: '120px',
      wert: (m) => stufe(m),
      inhalt: (m) => (stufe(m) === 'ERPROBUNG'
        ? <StatusBadge zustand="warnung" text="Erprobung" klein
                       titel="Erhält nur Aufträge, die ausdrücklich an diese Maschine gerichtet sind." />
        : <span className="schwach">produktiv</span>),
    },
    /* MASCH-3 (F248 E-4a): Fähigkeiten, z. B. „IFC-Export“ bei den Revit-VMs (sie sind die IFC-Exporter).
       Ein älterer Server liefert keine - dann „—“. */
    {
      schluessel: 'faehigkeiten', titel: 'Fähigkeiten', breite: '120px',
      wert: (m) => (m.faehigkeiten ?? []).map(maschinenfaehigkeitText).join(', '),
      inhalt: (m) => ((m.faehigkeiten ?? []).length > 0
        ? <>{(m.faehigkeiten ?? []).map((f) => (
            <StatusBadge key={f} zustand="neutral" text={maschinenfaehigkeitText(f)} klein />
          ))}</>
        : <span className="schwach">—</span>),
    },
    {
      schluessel: 'revit', titel: 'Revit', breite: '100px',
      wert: (m) => m.revitVersions,
      inhalt: (m) => <span className="schwach">{art(m) === 'REVIT' ? m.revitVersions : '—'}</span>,
    },

    /* Die Fassungen mit ihrem SOLL-Vergleich.
       Eine Fassungsnummer allein sagt nichts: '1.6.89' ist nur dann eine
       Auskunft, wenn danebensteht, dass 1.6.90 freigegeben ist. Genau
       daran ist am 17.08. eine halbe Stunde verloren gegangen — die
       Maschinen fuhren eine Fassung, die älter war als der Code, und man
       konnte es der Seite nicht ansehen. Seit U6a sagt es eine Marke mit
       Zeichen UND Text, nicht mehr eine rote Schriftfarbe allein. */
    {
      schluessel: 'agent', titel: 'Agent', breite: '150px',
      wert: (m) => m.agentVersion ?? '',
      inhalt: (m) => (m.agentAktuell
        ? <span className="schwach">{m.agentVersion ?? '—'}</span>
        : (
          <StatusBadge
            zustand="warnung" klein
            text={m.agentVersion ?? '—'}
            titel="Nicht die freigegebene Fassung."
          />
        )),
    },
    {
      schluessel: 'appbundle', titel: 'AppBundle', breite: '150px',
      wert: (m) => m.appBundleVersion ?? '',
      inhalt: (m) => (m.appBundleAktuell || art(m) !== 'REVIT'
        ? <span className="schwach">{art(m) === 'REVIT' ? m.appBundleVersion ?? '—' : '—'}</span>
        : (
          <StatusBadge
            zustand="warnung" klein
            text={m.appBundleVersion ?? '—'}
            titel={'Nicht die freigegebene Fassung. Die Maschine holt sie beim '
              + 'nächsten Leerlauf — ein laufender Auftrag wird nicht unterbrochen.'}
          />
        )),
    },
    {
      schluessel: 'fertig', titel: 'Fertig', art: 'zahl', breite: '90px',
      wert: (m) => m.completedCount,
    },
    {
      schluessel: 'fehler', titel: 'Fehler', art: 'zahl', breite: '90px',
      wert: (m) => m.failedCount,
      inhalt: (m) => (m.failedCount > 0
        ? <StatusBadge zustand="fehler" text={`${m.failedCount}`} klein
                       titel={`${m.failedCount} fehlgeschlagene Aufträge`} />
        : '0'),
    },
    {
      schluessel: 'aktionen', titel: '', breite: '230px', sortierbar: false,
      ausrichtung: 'rechts',
      inhalt: (m) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <button
            className="m-knopf m-knopf-nur-zeichen"
            disabled={laeuft || art(m) !== 'REVIT'}
            aria-label={`Agentenpaket für ${m.name} herunterladen`}
            title="Agentenpaket herunterladen (ohne Schlüssel)"
            onClick={(e) => {
              e.stopPropagation();
              api.agentPaketLaden(m.id, null, m.name);
            }}
          >
            <IconHerunterladen size={15} />
          </button>{' '}
          <button
            className="m-knopf m-knopf-nur-zeichen"
            disabled={laeuft}
            aria-label={`Zugangsschlüssel für ${m.name} erneuern`}
            title="Zugangsschlüssel erneuern"
            onClick={(e) => {
              e.stopPropagation();
              setSchluesselfrage(m);
            }}
          >
            <IconSchluessel size={15} />
          </button>{' '}
          <button
            className="m-knopf"
            disabled={laeuft}
            onClick={(e) => {
              e.stopPropagation();
              // Freigeben ist der harmlose Weg zurueck und braucht keine
              // Rueckfrage. Stilllegen nimmt der Verarbeitung eine Maschine -
              // bei zwei ist das die Haelfte.
              if (m.zustand === 3) {
                handeln(() => api.maschineAendern(m.id, { deaktiviert: false }));
              } else {
                setNachfrage({ maschine: m, art: 'stilllegen' });
              }
            }}
          >
            {m.zustand === 3 ? 'Freigeben' : 'Stilllegen'}
          </button>{' '}
          <button
            className="m-knopf"
            disabled={laeuft}
            onClick={(e) => {
              e.stopPropagation();
              setNachfrage({ maschine: m, art: 'entfernen' });
            }}
          >
            Entfernen
          </button>
        </span>
      ),
    },
  ], [api, handeln, laeuft]);

  const kopf = (
    <PageHeader
      titel="Maschinen"
      beschreibung={'Verarbeitungsmaschinen und Arbeitsplätze: welche Fassung sie '
        + 'fahren, wann sie sich zuletzt gemeldet haben und was sie geleistet haben.'}
      meta={bestand && <>{bestand.maschinen.length} Verarbeitungsmaschinen</>}
      aktionen={(
        <button className="m-knopf m-knopf-haupt" onClick={() => setAnlegen(true)}>
          <IconPlus size={16} />Maschine anlegen
        </button>
      )}
    />
  );

  if (fehler && !bestand) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Maschinenliste konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!bestand) {
    return (
      <>
        {kopf}
        <LoadingState text="Maschinen werden geladen …" zeilen={5} spalten={6} />
      </>
    );
  }

  const alle = bestand.maschinen;
  const maschinen = artFilter ? alle.filter((m) => art(m) === artFilter) : alle;

  return (
    <>
      {kopf}

      {schluessel && (
        <Schluesselanzeige
          api={api}
          daten={schluessel}
          schliessen={() => { setSchluessel(null); laden(); }}
        />
      )}

      {aktionsfehler !== null && (
        <ErrorState
          klein
          meldung="Die Aktion ist fehlgeschlagen."
          technisch={aktionsfehler}
        />
      )}

      {anlegen && (
        <MaschineAnlegen
          api={api}
          schliessen={() => setAnlegen(false)}
          fertig={(neu) => { setAnlegen(false); setSchluessel(neu); }}
        />
      )}

      <DataTable
          name="Verarbeitungsmaschinen"
          spalten={spalten}
          zeilen={maschinen}
          schluessel={(m) => m.id}
          suche={alle.length > 8}
          ausfuhr
          werkzeuge={(
            <select
              className="m-feld-eingabe"
              aria-label="Maschinenart filtern"
              value={artFilter}
              onChange={(e) => setArtFilter(e.target.value as Maschinenart | '')}
            >
              <option value="">Alle Arten</option>
              {MASCHINENARTEN.map((a) => <option key={a.wert} value={a.wert}>{a.text}</option>)}
            </select>
          )}
          kontextmenue={[
            {
              beschriftung: 'Golden-Attestierung und Readiness',
              beiWahl: (m) => setDetails(m),
              deaktiviert: (m) => !istGolden(m),
            },
            // MASCH-1 (F209 E2/E10): nach der Abnahme auf Produktiv, zurück in die Erprobung jederzeit.
            {
              beschriftung: 'Auf „produktiv“ stellen',
              beiWahl: (m) => handeln(() => api.maschineAendern(m.id, { betriebsstufe: 'PRODUKTIV' })),
              deaktiviert: (m) => laeuft || stufe(m) === 'PRODUKTIV',
            },
            {
              beschriftung: 'Auf „Erprobung“ stellen',
              beiWahl: (m) => handeln(() => api.maschineAendern(m.id, { betriebsstufe: 'ERPROBUNG' })),
              deaktiviert: (m) => laeuft || stufe(m) === 'ERPROBUNG',
            },
          ]}
          leer={alle.length > 0 ? (
            <EmptyState
              icon={<IconMaschine />}
              titel="Keine Maschine dieser Art."
              erklaerung="Der Filter blendet alle Maschinen aus."
            />
          ) : (
            <EmptyState
              icon={<IconMaschine />}
              titel="Noch keine Maschine angelegt."
              erklaerung={'Eine Verarbeitungsmaschine ist ein Windows-Rechner mit '
                + 'Revit, der Aufträge abarbeitet. Beim Anlegen erscheint ihr '
                + 'Zugangsschlüssel — einmalig.'}
              aktion={(
                <button className="m-knopf m-knopf-haupt" onClick={() => setAnlegen(true)}>
                  <IconPlus size={16} />Maschine anlegen
                </button>
              )}
            />
        )}
      />

      {/* Die Arbeitsstationen darunter: Dasselbe Muster, nur mit einem
          Menschen davor -- ein Rechner, der sich mit einem Schluessel
          meldet, eine Fassung faehrt und aktualisiert werden will. */}
      {/* MASCH-1 (F208, F209 E9): Höchstzahl gleichzeitiger Aufträge je Projekt oder Mandant. */}
      <Kontingente api={api} />

      <Arbeitsstationen api={api} />

      <Drawer
        offen={details !== null}
        titel={details ? `${details.name} — Golden-Attestierung` : ''}
        beschreibung="Attestierung und Readiness der Golden-Linie"
        beiSchliessen={() => setDetails(null)}
      >
        {details && <GoldenDetails maschine={details} />}
      </Drawer>

      <ConfirmDialog
        offen={schluesselfrage !== null}
        art="warnung"
        titel={`Zugangsschlüssel für ${schluesselfrage?.name ?? ''} erneuern?`}
        text={'Der bisherige gilt sofort nicht mehr. Die Maschine verliert die '
          + 'Verbindung, bis der neue in appsettings.json eingetragen ist — bis '
          + 'dahin verarbeitet sie nichts.'}
        bestaetigenText="Schlüssel erneuern"
        laeuft={laeuft}
        fehler={aktionsfehler}
        beiBestaetigen={async () => {
          const m = schluesselfrage;
          if (!m) return;
          setSchluesselfrage(null);
          await handeln(async () => setSchluessel(await api.schluesselErneuern(m.id)));
        }}
        beiAbbrechen={() => setSchluesselfrage(null)}
      />

      <MaschinenDialog
        nachfrage={nachfrage}
        laeuft={laeuft}
        fehler={aktionsfehler}
        abbrechen={() => setNachfrage(null)}
        bestaetigen={async () => {
          if (!nachfrage) return;
          const { maschine, art } = nachfrage;
          setNachfrage(null);
          await handeln(() => (art === 'entfernen'
            ? api.maschineEntfernen(maschine.id)
            : api.maschineAendern(maschine.id, { deaktiviert: true })));
        }}
      />
    </>
  );
}

/** MASCH-1 (F209 E1): Art der Maschine; ein älterer Server liefert sie nicht - dann Revit. */
function art(m: MaschineVerwaltung): Maschinenart {
  return m.maschinenart ?? 'REVIT';
}

/** MASCH-1 (F209 E2): Betriebsstufe; ohne Angabe produktiv. */
function stufe(m: MaschineVerwaltung): Betriebsstufe {
  return m.betriebsstufe ?? 'PRODUKTIV';
}

/**
 * MASCH-1 (F208, F209 E9): Kontingente. Je Projekt begrenzt die Vergabe die Zahl gleichzeitig laufender
 * Teilaufträge (weich: zwei Maschinen, die im selben Augenblick fragen, können sie um eins überschreiten).
 * Je Mandant ist die Grenze vorbereitet und greift erst mit der Mehrmandantenfähigkeit.
 */
function Kontingente({ api }: { api: ApiClient }) {
  const [zeilen, setZeilen] = useState<MaschinenKontingent[] | null>(null);
  const [projekte, setProjekte] = useState<Projekt[]>([]);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [bezug, setBezug] = useState<'projekt' | 'mandant'>('projekt');
  const [projektId, setProjektId] = useState('');
  const [mandant, setMandant] = useState('');
  const [kontingentArt, setKontingentArt] = useState<Maschinenart | ''>('');
  const [hoechst, setHoechst] = useState<number | null>(1);

  const laden = useCallback(() => {
    setFehler(null);
    return api.maschinenKontingente().then(setZeilen).catch(setFehler);
  }, [api]);

  useEffect(() => {
    laden();
    api.projekte().then(setProjekte).catch(() => setProjekte([]));
  }, [api, laden]);

  const handeln = async (was: () => Promise<unknown>) => {
    setLaeuft(true);
    setFehler(null);
    try {
      await was();
      await laden();
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };

  const spalten: Spalte<MaschinenKontingent>[] = [
    {
      schluessel: 'bezug', titel: 'Bezug', immerSichtbar: true,
      wert: (k) => (k.projectId ? `${k.projektNummer ?? ''} ${k.projektName ?? ''}` : `Mandant ${k.mandant ?? ''}`),
      inhalt: (k) => (k.projectId
        ? <><strong>{k.projektNummer ?? k.projectId}</strong> <span className="schwach">{k.projektName}</span></>
        : <>Mandant <strong>{k.mandant}</strong></>),
    },
    {
      schluessel: 'art', titel: 'Art', breite: '140px',
      wert: (k) => (k.maschinenart ? maschinenartText(k.maschinenart) : 'alle'),
    },
    {
      schluessel: 'hoechst', titel: 'Höchstens gleichzeitig', art: 'zahl', breite: '170px',
      wert: (k) => k.hoechstGleichzeitig,
    },
    {
      schluessel: 'wirkung', titel: 'Wirkung', breite: '190px',
      wert: (k) => (k.durchgesetzt ? 'durchgesetzt' : 'vorbereitet'),
      inhalt: (k) => (k.durchgesetzt
        ? <StatusBadge zustand="gut" text="durchgesetzt" klein />
        : <StatusBadge zustand="neutral" text="noch nicht durchgesetzt" klein
                       titel="Greift erst mit der Mehrmandantenfähigkeit." />),
    },
    {
      schluessel: 'aktionen', titel: '', breite: '120px', sortierbar: false, ausrichtung: 'rechts',
      inhalt: (k) => (
        <button
          className="m-knopf"
          disabled={laeuft}
          onClick={() => handeln(() => api.kontingentEntfernen(k.id))}
        >
          Entfernen
        </button>
      ),
    },
  ];

  const gueltig = hoechst !== null && hoechst >= 0
    && (bezug === 'projekt' ? projektId !== '' : mandant.trim() !== '');

  return (
    <FormSection
      titel="Kontingente"
      spalten={2}
      fuss={(
        <button
          className="m-knopf m-knopf-haupt"
          disabled={!gueltig || laeuft}
          onClick={() => handeln(async () => {
            await api.kontingentAnlegen({
              projectId: bezug === 'projekt' ? projektId : null,
              mandant: bezug === 'mandant' ? mandant.trim() : null,
              maschinenart: kontingentArt || null,
              hoechstGleichzeitig: hoechst ?? 0,
            });
            setProjektId('');
            setMandant('');
          })}
        >
          <IconPlus size={16} />Kontingent anlegen
        </button>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Die Kontingente konnten nicht geladen oder gespeichert werden."
                    technisch={fehler} />
      )}
      <Feld
        beschriftung="Bezug"
        art="auswahl"
        wert={bezug}
        beiAenderung={(w) => setBezug(w as 'projekt' | 'mandant')}
        optionen={[
          { wert: 'projekt', beschriftung: 'Projekt' },
          { wert: 'mandant', beschriftung: 'Mandant (vorbereitet)' },
        ]}
        hinweis="Alle Maschinen arbeiten als gemeinsamer Pool für alle Projekte. Ein Kontingent begrenzt nur, wie viele gleichzeitig an einem Projekt arbeiten."
      />
      {bezug === 'projekt' ? (
        <Feld
          beschriftung="Projekt"
          art="auswahl"
          wert={projektId}
          beiAenderung={(w) => setProjektId(w as string)}
          optionen={[{ wert: '', beschriftung: '— wählen —' },
            ...projekte.map((p) => ({ wert: p.id, beschriftung: `${p.number} ${p.name}` }))]}
        />
      ) : (
        <Feld
          beschriftung="Mandant"
          wert={mandant}
          beiAenderung={(w) => setMandant(w as string)}
          platzhalter="Kennung des Mandanten"
        />
      )}
      <Feld
        beschriftung="Maschinenart"
        art="auswahl"
        wert={kontingentArt}
        beiAenderung={(w) => setKontingentArt(w as Maschinenart | '')}
        optionen={[{ wert: '', beschriftung: 'alle Arten' },
          ...MASCHINENARTEN.map((a) => ({ wert: a.wert, beschriftung: a.text }))]}
      />
      <Feld
        beschriftung="Höchstens gleichzeitig"
        art="zahl"
        wert={hoechst}
        beiAenderung={(w) => setHoechst(w as number | null)}
        min={0}
        max={1000}
        hinweis="0 sperrt die Vergabe; laufende Aufträge bleiben unberührt."
      />
      <div style={{ gridColumn: '1 / -1' }}>
        <DataTable
          name="Kontingente"
          spalten={spalten}
          zeilen={zeilen ?? []}
          laedt={zeilen === null && fehler === null}
          schluessel={(k) => k.id}
          suche={false}
          leer={(
            <EmptyState
              icon={<IconMaschine />}
              titel="Keine Kontingente."
              erklaerung="Ohne Kontingent nimmt jede freie Maschine Aufträge aus jedem Projekt."
            />
          )}
        />
      </div>
    </FormSection>
  );
}

/**
 * Der gemeldete Zustand gilt nur, solange sich die Maschine meldet: Stürzt
 * der Agent ab, bleibt er auf „arbeitet" stehen.
 */
function maschinenzustand(m: MaschineVerwaltung): { text: string; zustand: Zustand } {
  if (m.zustand === 3) return { text: 'stillgelegt', zustand: 'neutral' };
  if (!m.erreichbar) return { text: 'offline', zustand: 'fehler' };
  if (m.zustand === 2) return { text: 'arbeitet', zustand: 'info' };
  return { text: 'bereit', zustand: 'gut' };
}

function goldenBereit(m: MaschineVerwaltung) {
  return istGolden(m) && m.erreichbar && m.goldenReady;
}

interface GoldenAttestation {
  goldenRoot?: string | null;
  agentVersion?: string | null;
  automationVersion?: string | null;
  addinVersion?: string | null;
  updaterVersion?: string | null;
  appBundleReleaseVersion?: string | null;
  revitVersion?: string | null;
  gitCommit?: string | null;
  agentGitCommit?: string | null;
  checks?: Record<string, boolean> | null;
}

function istGolden(maschine: MaschineVerwaltung) {
  return maschine.environment?.toUpperCase() === 'GOLDEN';
}

function jsonLesen<T>(wert: string | null): T | null {
  if (!wert) return null;
  try { return JSON.parse(wert) as T; }
  catch { return null; }
}

function readinessGruendeLesen(wert: string | null) {
  const gelesen = jsonLesen<unknown>(wert);
  return Array.isArray(gelesen)
    ? gelesen.filter((grund): grund is string => typeof grund === 'string')
    : [];
}

function kurzCommit(commit: string | null | undefined) {
  return commit ? commit.slice(0, 12) : '—';
}

function GoldenDetails({ maschine }: { maschine: MaschineVerwaltung }) {
  const attestierung = jsonLesen<GoldenAttestation>(maschine.goldenAttestationJson);
  const gruende = readinessGruendeLesen(maschine.goldenReadinessReasonsJson);
  const fehlendeChecks = Object.entries(attestierung?.checks ?? {})
    .filter(([, bestanden]) => !bestanden)
    .map(([name]) => name);

  const eintraege: Kennwert[] = [
    { beschriftung: 'Environment', wert: maschine.environment },
    { beschriftung: 'Machine', wert: maschine.machineName },
    { beschriftung: 'Rolle', wert: maschine.goldenRole },
    { beschriftung: 'GoldenRoot', wert: attestierung?.goldenRoot, breit: true },
    { beschriftung: 'Agent', wert: attestierung?.agentVersion ?? maschine.agentVersion },
    { beschriftung: 'Updater', wert: attestierung?.updaterVersion },
    { beschriftung: 'Automation', wert: attestierung?.automationVersion },
    { beschriftung: 'Add-in', wert: attestierung?.addinVersion },
    {
      beschriftung: 'AppBundle',
      wert: attestierung?.appBundleReleaseVersion ?? maschine.appBundleVersion,
    },
    { beschriftung: 'Revit', wert: attestierung?.revitVersion ?? maschine.revitVersions },
    {
      beschriftung: 'Bundle-Commit', wert: kurzCommit(attestierung?.gitCommit),
      hinweis: attestierung?.gitCommit ?? undefined,
    },
    {
      beschriftung: 'Agent-Commit', wert: kurzCommit(attestierung?.agentGitCommit),
      hinweis: attestierung?.agentGitCommit ?? undefined,
    },
  ];

  const nichtBereit = [
    ...new Set([...gruende, ...fehlendeChecks.map((c) => `${c}_FAILED`)]),
  ];

  return (
    <>
      <p>
        {nichtBereit.length > 0
          ? <StatusBadge zustand="fehler" text="Nicht bereit" />
          : maschine.goldenReady
            ? <StatusBadge zustand="gut" text="Alle Pflichtchecks bestanden" />
            : <StatusBadge zustand="neutral" text="Nichts gemeldet" />}
      </p>

      {nichtBereit.length > 0 && (
        <p className="schwach" style={{ fontSize: 12 }}>{nichtBereit.join(', ')}</p>
      )}

      <PropertyGrid spalten={2} eintraege={eintraege} />
    </>
  );
}

function MaschineAnlegen({
  api, schliessen, fertig,
}: {
  api: ApiClient;
  schliessen: () => void;
  fertig: (m: NeueMaschine) => void;
}) {
  const [name, setName] = useState('');
  const [versionen, setVersionen] = useState('2025');
  const [vermerk, setVermerk] = useState('');
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);
  // MASCH-1 (F209 E1/E2/E10): Revit wie bisher produktiv; ein neuer Worker beginnt in der Erprobung.
  const [neueArt, setNeueArt] = useState<Maschinenart>('REVIT');
  const [neueStufe, setNeueStufe] = useState<Betriebsstufe>('PRODUKTIV');

  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      fertig(await api.maschineAnlegen({
        name: name.trim(),
        revitVersions: neueArt === 'REVIT' ? versionen.trim() : undefined,
        note: vermerk.trim() || undefined,
        maschinenart: neueArt,
        betriebsstufe: neueStufe,
      }));
    } catch (e) {
      setFehler(e);
      setLaeuft(false);
    }
  };

  return (
    <FormSection
      titel="Neue Maschine"
      spalten={2}
      fuss={(
        <>
          <button className="m-knopf" onClick={schliessen}>Abbrechen</button>
          <button
            className="m-knopf m-knopf-haupt"
            onClick={speichern}
            disabled={!name.trim() || laeuft}
          >
            {laeuft ? 'wird angelegt …' : 'Anlegen'}
          </button>
        </>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Die Maschine konnte nicht angelegt werden."
                    technisch={fehler} />
      )}

      <Feld
        beschriftung="Name"
        wert={name}
        beiAenderung={(w) => setName(w as string)}
        platzhalter="REVIT-VM-03"
        pflicht
        hinweis="Erscheint im Protokoll jedes Auftrags — am besten der Rechnername."
      />
      <Feld
        beschriftung="Art"
        art="auswahl"
        wert={neueArt}
        beiAenderung={(w) => {
          const gewaehlt = w as Maschinenart;
          setNeueArt(gewaehlt);
          setNeueStufe(gewaehlt === 'REVIT' ? 'PRODUKTIV' : 'ERPROBUNG');
        }}
        optionen={MASCHINENARTEN.map((a) => ({ wert: a.wert, beschriftung: a.text }))}
        hinweis="Nur Revit-Maschinen erhalten Revit-Aufträge."
      />
      <Feld
        beschriftung="Stufe"
        art="auswahl"
        wert={neueStufe}
        beiAenderung={(w) => setNeueStufe(w as Betriebsstufe)}
        optionen={[
          { wert: 'PRODUKTIV', beschriftung: 'produktiv' },
          { wert: 'ERPROBUNG', beschriftung: 'Erprobung' },
        ]}
        hinweis="In der Erprobung nur Aufträge, die ausdrücklich an diese Maschine gerichtet sind."
      />
      {neueArt === 'REVIT' && (
        <Feld
          beschriftung="Revit-Versionen"
          wert={versionen}
          beiAenderung={(w) => setVersionen(w as string)}
          platzhalter="2025"
          hinweis="Mehrere durch Komma. Bestimmt, welche Aufträge die Maschine bekommt."
        />
      )}
      <Feld
        beschriftung="Vermerk"
        wert={vermerk}
        beiAenderung={(w) => setVermerk(w as string)}
        platzhalter="Azure, West Europe"
      />
    </FormSection>
  );
}

/**
 * Zeigt den Zugangsschluessel - das einzige Mal.
 *
 * Bewusst aufdringlich gestaltet: Wer diesen Kasten wegklickt, ohne den
 * Schluessel zu sichern, muss ihn erneuern lassen.
 */
function Schluesselanzeige({
  api, daten, schliessen,
}: {
  api: ApiClient;
  daten: NeueMaschine;
  schliessen: () => void;
}) {
  const [kopiert, setKopiert] = useState(false);

  return (
    <div className="karte" style={{ borderColor: 'var(--warnung)', background: 'var(--warn-flaeche)' }}>
      <h2 className="karte-titel" style={{ color: '#5d4500' }}>
        <IconSchluessel />Zugangsschluessel fuer {daten.name}
      </h2>

      <p style={{ color: '#5d4500', marginTop: 0 }}>{daten.hinweis}</p>

      <div style={{
        background: 'var(--weiss)',
        border: '1px solid var(--warn-rand, #e6c060)',
        borderRadius: 3,
        padding: '12px 14px',
        fontFamily: 'monospace',
        fontSize: 13,
        wordBreak: 'break-all',
        margin: '12px 0',
      }}>
        {daten.schluessel}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          className="m-knopf"
          onClick={() => {
            // Über den Helfer, mit Rückfallweg: Der Schlüssel ist nur einmal
            // sichtbar, und ein Kopieren, das stillschweigend scheitert, kostet
            // eine neue Einrichtung.
            void inDieAblage(daten.schluessel).then((geklappt) => {
              if (!geklappt)
                alert('Der Browser erlaubt das Kopieren nicht. Der Schlüssel:'
                    + '\n\n' + daten.schluessel);
            });
            setKopiert(true);
            setTimeout(() => setKopiert(false), 2500);
          }}
        >
          {kopiert ? 'kopiert' : 'In die Zwischenablage'}
        </button>

        <button
          className="m-knopf m-knopf-haupt"
          onClick={() => api.agentPaketLaden(daten.id, daten.schluessel, daten.name)}
        >
          Agentenpaket mit Schlüssel herunterladen
        </button>

        <button className="m-knopf" style={{ marginLeft: 'auto' }} onClick={schliessen}>
          Schließen
        </button>
      </div>

      <p className="sehr-schwach" style={{ marginBottom: 0, color: '#7a6000' }}>
        Im Paket ist der Schlüssel bereits in der appsettings.json eingetragen -
        auf der Maschine muss dann nur noch der Pfad zu Revit stimmen.
      </p>
    </div>
  );
}

/**
 * Fragt vor dem Stilllegen oder Entfernen einer Maschine nach.
 *
 * Beim Entfernen ist die Eingabe des Namens verlangt: Es ist der einzige
 * Schritt hier, der sich nicht zurücknehmen lässt, und bei mehreren ähnlich
 * benannten Maschinen ist ein Fehlklick sonst schnell passiert. `ConfirmDialog`
 * kann das über `bestaetigungswort`.
 */
function MaschinenDialog({
  nachfrage, laeuft, fehler, abbrechen, bestaetigen,
}: {
  nachfrage: { maschine: MaschineVerwaltung; art: 'stilllegen' | 'entfernen' } | null;
  laeuft: boolean;
  fehler: unknown;
  abbrechen: () => void;
  bestaetigen: () => Promise<void>;
}) {
  const entfernt = nachfrage?.art === 'entfernen';
  const name = nachfrage?.maschine.name ?? '';

  return (
    <ConfirmDialog
      offen={nachfrage !== null}
      art={entfernt ? 'loeschen' : 'warnung'}
      titel={entfernt ? `${name} entfernen?` : `${name} stilllegen?`}
      bestaetigenText={entfernt ? 'Entfernen' : 'Stilllegen'}
      bestaetigungswort={entfernt ? name : undefined}
      laeuft={laeuft}
      fehler={fehler}
      beiBestaetigen={bestaetigen}
      beiAbbrechen={abbrechen}
    >
      <PropertyGrid
        spalten={1}
        eintraege={[
          {
            beschriftung: 'Was geschieht',
            wert: entfernt
              ? 'Die Maschine wird aus der Verwaltung gelöscht. Ihr '
                + 'Zugangsschlüssel gilt nicht mehr — der Agent auf der Maschine '
                + 'meldet sich ab sofort erfolglos.'
              : 'Die Maschine bekommt keine neuen Aufträge mehr. Ein laufender '
                + 'Auftrag wird nicht unterbrochen.',
            breit: true,
          },
          {
            beschriftung: 'Was bleibt',
            wert: 'Alle bisherigen Aufträge und ihre Dateien. Auf der Maschine '
              + 'selbst ändert sich nichts — Agent und Revit bleiben installiert.',
            breit: true,
          },
          {
            beschriftung: 'Rückgängig',
            wert: entfernt
              ? 'Nicht möglich. Die Maschine müsste neu angelegt und ihr '
                + 'Zugangsschlüssel dort eingetragen werden.'
              : 'Jederzeit über „Freigeben".',
            breit: true,
          },
        ]}
      />
    </ConfirmDialog>
  );
}
